import { z } from "zod";

export const orderItemSchema = z.object({
  name: z.string().min(1, "Name is required"),
  quantity: z.coerce.number().min(0, "Quantity is required"),
  uom: z.string().nullable().optional().default(null),
  currency: z.string().nullable().optional(),
  amount: z.coerce.number().nullable().optional(),
  product_id: z.coerce.number().nullable().default(null),
  sku: z.string().nullable().default(null),
  line_item_id: z.number().nullable().optional(),
  manual_override: z.boolean().default(false),
});

export type OrderItem = z.infer<typeof orderItemSchema>;

export const orderItemsSchema = z.array(orderItemSchema);

export type OrderItems = z.infer<typeof orderItemsSchema>;

function parseOrderDetailsString(rawValue: string): unknown {
  const trimmedValue = rawValue.trim();
  if (trimmedValue.length === 0) {
    return [];
  }

  let pythonJsonCandidate = trimmedValue.replace(/'/g, '"');
  pythonJsonCandidate = pythonJsonCandidate.replace(/\bNone\b/g, "null");
  pythonJsonCandidate = pythonJsonCandidate.replace(/\bTrue\b/g, "true");
  pythonJsonCandidate = pythonJsonCandidate.replace(/\bFalse\b/g, "false");

  const parseCandidates = [trimmedValue, pythonJsonCandidate];
  for (const parseCandidate of parseCandidates) {
    try {
      return JSON.parse(parseCandidate);
    } catch (error) {
      void error;
    }
  }

  return [];
}

function normalizeOrderDetails(value: unknown): OrderItems {
  const parsedValue =
    typeof value === "string" ? parseOrderDetailsString(value) : value;

  if (!Array.isArray(parsedValue)) {
    return [];
  }

  const normalizedItems: OrderItems = [];
  for (const candidateItem of parsedValue) {
    const parsedItem = orderItemSchema.safeParse(candidateItem);
    if (parsedItem.success) {
      normalizedItems.push(parsedItem.data);
    }
  }

  return normalizedItems;
}

export const orderSchema = z.object({
  id: z.number(),
  supplier_order_number: z.number().nullable().optional(),
  source: z.string(),
  order_type: z.enum(["external", "internal"]).default("external"),
  status: z.enum(["Pending", "Approved", "Rejected", "Dispatched", "Delivered"]),
  restaurant_name: z.string(),
  external_po_number: z.string().nullable(),
  internal_po_number: z.string(),
  erp_order_id: z.string().nullable().optional(),
  order_datetime: z.string(),
  order_datetime_recieved: z.string().nullable(),
  expected_delivery_date: z.string().nullable(),
  restaurant_branch: z.string().nullable(),
  restaurant_address: z.string(),
  order_details: z.preprocess(normalizeOrderDetails, orderItemsSchema),
  branch_address: z.string(),
  supplier: z.number(),
  created_by: z.number().nullable(),
  warehouse_id: z.number().nullable().optional(),
  destination_warehouse_id: z.number().nullable().optional(),
  matched_customer_contact_id: z.string().nullable().optional(),
  matched_customer_contact_name: z.string().nullable().optional(),
  matched_customer_approved: z.boolean().nullable().optional(),
  matched_customer_approved_at: z.string().nullable().optional(),
});

export type Order = z.infer<typeof orderSchema>;

export const ordersSchema = z.array(orderSchema);

export type Orders = z.infer<typeof ordersSchema>;

export const dashboardDataSchema = z.object({
  total_orders: z.number(),
  total_orders_change: z.number(),
  pending_orders: z.number(),
  pending_orders_change: z.number(),
  processed_orders: z.number(),
  processed_orders_change: z.number(),
  errors: z.number(),
  errors_change: z.number(),
  this_month_orders: z.number(),
  last_month_orders: z.number(),
});

export type DashboardData = z.infer<typeof dashboardDataSchema>;

export type NormalizedOrderItem = {
  name: string;
  quantity: string;
  unit: string;
  amount: string;
  currency: string;
  product_id: string;
  sku: string;
  line_item_id: string;
};

/** DRF-style pagination wrapper for orders */
export const paginatedOrdersSchema = z.object({
  count: z.number(),
  next: z.string().nullable(),
  previous: z.string().nullable(),
  results: z.array(orderSchema),
});

export type PaginatedOrders = z.infer<typeof paginatedOrdersSchema>;

/** Generic TS helper you can reuse elsewhere */
export type Paginated<T> = {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
};
